import { useEffect } from "react";

/**
 * Lightweight, robust SEO component for React 19.
 * Dynamically updates document title, meta description, canonical URL,
 * Open Graph, Twitter cards, and Schema.org JSON-LD scripts on route change.
 */
const SEO = ({
  title,
  description,
  canonicalUrl,
  keywords,
  ogType = "website",
  ogImage = "https://raghavendradoorsandhardware.in/assets/prelaminated.png",
  schemaData = null,
}) => {
  const siteTitle = "Sree Raghavendra Doors, Plywood & Hardware | Rajahmundry";
  const fullTitle = title ? `${title} | Sree Raghavendra` : siteTitle;
  const siteUrl = "https://raghavendradoorsandhardware.in";
  const fullCanonical = canonicalUrl
    ? canonicalUrl.startsWith("http")
      ? canonicalUrl
      : `${siteUrl}${canonicalUrl}`
    : siteUrl;

  useEffect(() => {
    // 1. Update Title
    document.title = fullTitle;

    // Helper to update or create a meta tag
    const setMetaTag = (attrName, attrValue, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Helper to update or create a link tag
    const setLinkTag = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // 2. Set Meta Tags
    setMetaTag("name", "description", description);
    if (keywords) {
      setMetaTag("name", "keywords", keywords);
    }
    setMetaTag("name", "robots", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");

    // 3. Set Canonical Link
    setLinkTag("canonical", fullCanonical);

    // 4. Set Open Graph Meta Tags
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", fullCanonical);
    setMetaTag("property", "og:type", ogType);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:site_name", "Sree Raghavendra Doors, Plywood & Hardware");
    setMetaTag("property", "og:locale", "en_IN");

    // 5. Set Twitter Meta Tags
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", ogImage);

    // 6. Inject Dynamic Schema.org JSON-LD
    let scriptEl = document.getElementById("route-schema-jsonld");
    if (schemaData) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.id = "route-schema-jsonld";
        scriptEl.type = "application/ld+json";
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schemaData);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      // Optional cleanup on unmount
      const existingScript = document.getElementById("route-schema-jsonld");
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [fullTitle, description, fullCanonical, keywords, ogType, ogImage, schemaData]);

  return null;
};

export default SEO;
